import React from 'react';
import { Link } from "react-router-dom";
import routes from 'src/routes';
import url from 'src/util/url';
import Page from 'src/components/Page';
import ExpandDoc from 'src/components/ExpandDoc';
import CodeSamples from 'src/components/CodeSamples';
import SpecLink from 'src/components/SpecLink';
import TxnTokenFlow from './TxnTokenFlow';

function TxnSimpleJavaScriptPage() {
    return <Page header="JavaScript - Simple">
        <p>
            The JavaScript Simple integration method allows you to integrate with minimal JavaScript code.
            We handle the creation of the form and the submission of card data.
        </p>
        <p>
            Processing a payment using the JavaScript Simple approach requires that you:
            <ol>
                <li><SpecLink uriFragment="#operation/TxnAuthkey_CreateAuthkey">Create a transaction authkey</SpecLink></li>
                <li><SpecLink uriFragment="#operation/TxnAuthkey_AttachTxnDetails">Attach txn details</SpecLink></li>
                <li><i>(Optional)</i> <SpecLink uriFragment="#operation/TxnAuthkey_AttachPaymentMethod">Attach payment method and pass in the Token number to process payment via a token</SpecLink></li>
                <li>Using <b>JavaScript Simple</b>, attach payment details.</li>
                <li><SpecLink uriFragment="#operation/TxnAuthkey_Process">Process transaction</SpecLink></li>
            </ol> 
        </p>

        <h2>Attaching payment details using JavaScript Simple</h2>

        <h3>Html page</h3>
        <h4>Add JavaScript</h4>
        <p>To enable JavaScript integration, you must include the following script tag in your page.</p>
        <CodeSamples
            samples={[
                {
                    language: 'html',
                    content: `<script src="${url.api("clientscripts/api.js")}" type="text/javascript"></script>`
                }]} copyButton />

        <h4>Add payment control</h4>
        <p>Include the html tag to display the payment control.</p>
        <CodeSamples
            samples={[
                {
                    language: 'html',
                    content: `<div id="cardForm"></div>`
                }]} copyButton />

        <h4>Add styling</h4>
        <p>Include the following style tag in your page to use our form styling.</p>
        <CodeSamples
            samples={[
                {
                    language: 'html',
                    content: `<link href="${url.api("clientscripts/api.css")}" rel="stylesheet" />`
                }]} copyButton />

        <h3>JavaScript</h3>
        <p>After obtaining the AuthKey and attaching transaction details, setup the Payment form.</p>

        <p>Here is an example for calling <code>setupPaymentMethodForm</code>:</p>

        <p>If <b>Authentication (3D Secure)</b> is active with your integration, enable <code>name</code> property for Cardholder name to be completed by customer.</p>

        <CodeSamples
            samples={[               
                {
                    language: 'javascript',
                    content: `
BPOINT.txn.authkey.setupPaymentMethodForm(authkey, {
    appendToElementId: "cardForm",
    card: {
        number: {
            label: "Card number"
        },
        expiry: {
            label: "Expiry"
        },
        cvn: {
            label: "Cvn"
        },
        name: {
            label: "Name",
            hide: false
        }
    },
    clientsideValidation: true,
    callback: function (code, data) {        
        if (code === "success") {
            // proceed with payment / show surcharges                                
        } else {
            // show an error to the user                                
        }
    }
});    
`
                }
            ]} copyButton
        />

        <h4>BPOINT.txn.authkey.setupPaymentMethodForm Specification</h4>
        <p>You can follow the specification to customise the form to your liking.</p>

        <ExpandDoc
            method="BPOINT.txn.authkey.setupPaymentMethodForm"
            doc={{
                authkey: {
                    type: 'string',
                    description: "Transaction authkey to attach payment method to"
                },
                options: {
                    type: "object",
                    description: "Form settings",
                    children: {
                        appendToElementId: {
                            type: 'string',
                            description: "The element to append the form to",
                            mandatory: true
                        },
                        callback: {
                            type: "function",
                            description: "A callback function that is executed when the submission result is received",
                            mandatory: true,
                            children: {
                                code: {
                                    type: "string",
                                    description: <>One of <code>success</code>, <code>error</code>, or <code>timeout</code></>
                                },
                                data: {
                                    type: "object",
                                    description: <>See response of <Link to={routes.api.spec + "#operation/TxnAuthkeyClient_AttachPaymentMethod"}>TxnAuthkeyClient - AttachPaymentMethod</Link></>
                                }
                            }
                        },
                        card: {
                            type: 'object',
                            description: 'Card field settings',
                            children: {
                                number: {
                                    type: 'object',
                                    description: 'Card number settings',
                                    children: {
                                        label: {
                                            type: 'string',
                                            description: 'Custom label',
                                            default: 'Card number'
                                        },
                                    }
                                },
                                expiry: {
                                    type: 'object',
                                    description: 'Card expiry settings',
                                    children: {
                                        label: {
                                            type: 'string',
                                            description: 'Custom label',
                                            default: 'Expiry'
                                        },
                                    }
                                },
                                cvn: {
                                    type: 'object',
                                    description: 'CVN settings',
                                    children: {
                                        label: {
                                            type: 'string',
                                            description: 'Custom label',
                                            default: 'CVN'
                                        },
                                    }
                                },
                                name: {
                                    type: 'object',
                                    description: 'Name on card settings',
                                    children: {
                                        label: {
                                            type: 'string',
                                            description: 'Custom label',
                                            default: 'Name on card'
                                        },
                                        hide: {
                                            type: 'boolean',
                                            description: 'Whether to hide the field'
                                        },
                                    }
                                },
                            }
                        },
                        bank: {
                            type: 'object',
                            description: 'Bank account settings (only used in token flow)',
                            children: {
                                bsb: {
                                    type: 'object',
                                    description: 'BSB settings',
                                    children: {
                                        label: {
                                            type: 'object',
                                            description: 'Custom label',
                                            default: 'BSB'
                                        }
                                    }
                                },
                                account: {
                                    type: 'object',
                                    description: 'Account number settings',
                                    children: {
                                        label: {
                                            type: 'object',
                                            description: 'Custom label',
                                            default: 'Account number'
                                        }
                                    }
                                },
                                name: {
                                    type: 'object',
                                    description: 'Account name settings',
                                    children: {
                                        label: {
                                            type: 'object',
                                            description: 'Custom label',
                                            default: 'Account name'
                                        }
                                    }
                                }
                            }
                        },
                        clientsideValidation: {
                            type: 'boolean',
                            description: 'Whether to perform client-side validation as the user types'
                        },
                        disableSubmitButtonOnClick: {
                            type: 'boolean',
                            description: 'If set to true, the Submit button will be disabled once payment details are accepted'
                        },
                        onFormLoaded: {
                            type: 'function',
                            description: 'A callback function that is executed when the form has loaded'
                        },
                    }
                }
            }}
        />

        { TxnTokenFlow("JavaScript Simple") }        
   
    </Page>
}

export default TxnSimpleJavaScriptPage;